import { Channel } from '@src/api/shared/community/channelApiClient';
import { ChannelMember } from '@src/api/shared/community/channelMemberApiClient';
import { LinkPreviewResponse, PinnedChat, ThreadChat } from '@src/api/shared/community/chatApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import { Attachment } from '@src/idb';
import { createContext, ReactNode, useContext } from 'react';

import { ChatMenuPermission } from '../components/chat/ChatMessage';

interface ThreadContextType {
  // Thread state
  isThreadOpen: boolean;
  setIsThreadOpen: (isOpen: boolean) => void;
  activeThreadParentId: number | null;
  setActiveThreadParentId: (id: number | null) => void;
  parentChat?: ThreadChat;
  setParentChat?: (parentChat: ThreadChat) => void;
  threadReplies: ThreadChat[];
  setThreadReplies: (replies: ThreadChat[]) => void;
  threadPanelWidth: number;
  setThreadPanelWidth: (width: number) => void;

  // Editing state
  threadEditingChatId: number | null;
  setThreadEditingChatId: (id: number | null) => void;
  threadEditingMessage: string;
  setThreadEditingMessage: (message: string) => void;
  threadEditingAttachments: Attachment[];
  setThreadEditingAttachments: (attachments: Attachment[]) => void;

  // Creating state
  threadCreatingMessage: string;
  setThreadCreatingMessage: (message: string) => void;
  threadCreatingAttachments: Attachment[];
  setThreadCreatingAttachments: (attachments: Attachment[]) => void;
  threadIsAnonymous: boolean;
  setThreadIsAnonymous: (isAnonymous: boolean) => void;

  // Channel context
  channel: Channel | null; // Add the channel object
  channelMembers: ChannelMember[];
  pinnedChat?: PinnedChat;
  linkPreviewCache: Record<string, LinkPreviewResponse | null>;

  // Permissions
  isLoggedIn: boolean;

  // User state
  currentUserId: number | undefined;
  currentUserType: ChannelMemberUserTypeEnum | undefined;

  // UI state
  isActionMenuSpOpen: boolean;
  currentChatIdSp: number | undefined;
  viewportHeight: number;
  showThreadUnreadLines: boolean;
  setShowThreadUnreadLines: (show: boolean) => void;

  // Callbacks
  onChatEditClick: (chat: ThreadChat) => void;
  onChatDeleteClick: (chat: ThreadChat) => void;
  onChatReportClick: (chat: ThreadChat) => void;
  onSetCurrentChatIdSp: (id: number | undefined) => void;
  onPinnedChatMenuClose: () => void;
  onPinnedChatLongPress: () => void;
  onToggleLinkPreview: (chatId: number, hidePreview: boolean) => void;
  onPinClick: (chat_id: number | undefined) => Promise<void>;
  getChatMenuPermission: (chat: ThreadChat) => ChatMenuPermission;
  shouldShowUnreadLine: (chatId: number) => boolean;
  initialThreadChatId?: number;
  setInitialThreadChatId?: (id: number | undefined) => void;
  firstUnreadThreadChatId: number | null;
}

const ThreadContext = createContext<ThreadContextType | undefined>(undefined);

export const useThread = () => {
  const context = useContext(ThreadContext);
  if (!context) {
    throw new Error('useThread must be used within ThreadProvider');
  }
  return context;
};

interface ThreadProviderProps {
  children: ReactNode;
  value: ThreadContextType;
}

export const ThreadProvider = ({ children, value }: ThreadProviderProps) => {
  return <ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>;
};
